/**
 * The settlement engine (0.13): meter consumption costed against versioned
 * unit prices, offset against the deposit.
 *
 * Two rules from `.claude/rules/domain.md` shape everything here:
 * - **Unit prices are versioned, never mutated.** Consumption is costed at the
 *   price valid on the STAY date — a settlement computed in 2027 must still
 *   reproduce identically in 2030, whatever the price is by then.
 * - Money is integer cents through `money.ts`; meter values are not money and
 *   may carry decimals (the meters have three).
 */

import { addCents, type Cents, cents, subtractCents } from "./money.js";

export const METERS = ["electricity", "gas", "water"] as const;
export type MeterKind = (typeof METERS)[number];

export type UnitPrice = {
  meter: MeterKind;
  /** "kWh" | "m3" — display only, never computed with. */
  unit: string;
  priceCents: number;
  /** PlainDate string. The version applies from this date until a later one starts. */
  validFrom: string;
};

/**
 * The version valid on `date`: the latest one that started on or before it.
 * Undefined is an expected outcome (a meter without any price configured),
 * not an exception — the caller decides whether that blocks anything.
 */
export function unitPriceOn(
  prices: readonly UnitPrice[],
  meter: MeterKind,
  date: string,
): UnitPrice | undefined {
  let latest: UnitPrice | undefined;
  for (const price of prices) {
    if (price.meter !== meter || price.validFrom > date) continue;
    if (!latest || price.validFrom > latest.validFrom) latest = price;
  }
  return latest;
}

export type Meterstanden = { arrival?: number; departure?: number };

export type AfrekeningLine = {
  meter: MeterKind;
  unit: string;
  /** Departure minus arrival, in the meter's own unit. */
  verbruik: number;
  unitCents: Cents;
  amountCents: Cents;
};

export type AfrekeningFout = {
  meter: MeterKind;
  reason: "negatief_verbruik" | "geen_eenheidsprijs";
};

export type Afrekening = {
  /** One line per meter with both readings and a usable price. */
  lines: AfrekeningLine[];
  /** Meters still missing a reading — normal mid-flow, blocks settling. */
  onvolledig: MeterKind[];
  fouten: AfrekeningFout[];
  consumptionCents: Cents;
  damageCents: Cents;
  depositAppliedCents: Cents;
  /**
   * deposit − consumption − damage, mirroring the GENERATED column on the
   * settlement table. Positive: refund the renter. Negative: the renter owes.
   */
  balanceCents: Cents;
  /** True when every meter produced a line — the only state that may settle. */
  compleet: boolean;
};

/**
 * Always returns a structure, never a refusal: the screen renders the running
 * result while readings are still being typed, and `compleet` gates the
 * settle action (the API re-checks it server-side).
 */
export function berekenAfrekening(input: {
  standen: Partial<Record<MeterKind, Meterstanden>>;
  prijzen: readonly UnitPrice[];
  /** The arrival date — consumption is costed on the stay, not on today. */
  stayDate: string;
  depositReceivedCents: Cents;
  damageCents: Cents;
}): Afrekening {
  const lines: AfrekeningLine[] = [];
  const onvolledig: MeterKind[] = [];
  const fouten: AfrekeningFout[] = [];
  let consumptionCents = cents(0);

  for (const meter of METERS) {
    const standen = input.standen[meter];
    if (standen?.arrival === undefined || standen.departure === undefined) {
      onvolledig.push(meter);
      continue;
    }
    // Readings carry at most three decimals, so the whole computation runs in
    // integer THOUSANDTHS — a float `verbruik * priceCents` costs a cent at
    // the .5 boundary (450 × 1.13 is 508.4999… in binary), and no float may
    // sit in a price path (CLAUDE.md).
    const duizendsten = Math.round(standen.departure * 1000 - standen.arrival * 1000);
    if (duizendsten < 0) {
      // A departure below the arrival is a misread, not negative consumption.
      fouten.push({ meter, reason: "negatief_verbruik" });
      continue;
    }
    const price = unitPriceOn(input.prijzen, meter, input.stayDate);
    if (!price) {
      fouten.push({ meter, reason: "geen_eenheidsprijs" });
      continue;
    }
    // Half-up exactly once per line, in integer arithmetic. Worked examples:
    // 282,500 kWh × € 0,42 = € 118,65 · 1,130 m³ × € 4,50 = 508,5 c → € 5,09.
    const verbruik = duizendsten / 1000;
    const amountCents = cents(Math.round((price.priceCents * duizendsten) / 1000));
    lines.push({
      meter,
      unit: price.unit,
      verbruik,
      unitCents: cents(price.priceCents),
      amountCents,
    });
    consumptionCents = addCents(consumptionCents, amountCents);
  }

  const balanceCents = subtractCents(
    input.depositReceivedCents,
    addCents(consumptionCents, input.damageCents),
  );

  return {
    lines,
    onvolledig,
    fouten,
    consumptionCents,
    damageCents: input.damageCents,
    depositAppliedCents: input.depositReceivedCents,
    balanceCents,
    compleet: onvolledig.length === 0 && fouten.length === 0,
  };
}

export type WaarborgVerrekening = {
  status: "partly_withheld" | "refunded" | "forfeited";
  withheldCents: Cents;
  refundedCents: Cents;
  /** Stored on the deposit row — document data, like the audit labels. */
  withheldReason: string | null;
};

/**
 * Withhold what is owed, refund the rest. Never more than came in — the
 * remainder would be an extra invoice, which Phase 0 does not issue yet.
 * Worked example: € 300,00 received, € 163,65 owed → € 163,65 withheld,
 * € 136,35 refunded.
 *
 * Only meaningful when something was actually received: with € 0,00 in there
 * is nothing to split, and every status this returns violates the deposit
 * table's CHECKs on a zero-received row. The caller must leave the deposit
 * row untouched in that case.
 */
export function verrekenWaarborg(receivedCents: Cents, owedCents: Cents): WaarborgVerrekening {
  const withheld = cents(Math.min(owedCents, receivedCents));
  const refunded = subtractCents(receivedCents, withheld);
  return {
    // Everything kept is `forfeited`, not "partly": the deposit CHECKs allow
    // it, and "partly withheld, € 0,00 refunded" reads as a contradiction.
    status: withheld === 0 ? "refunded" : refunded === 0 ? "forfeited" : "partly_withheld",
    withheldCents: withheld,
    refundedCents: refunded,
    withheldReason:
      withheld > 0 ? "Verbruik en eventuele schade ingehouden, zie eindafrekening" : null,
  };
}
